import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LevelBadge } from "../gamification/LevelBadge";
import { ExternalLink, Github, Linkedin, Globe, ImageIcon } from "lucide-react";
import { UserProfilePreview } from "@/types/userProfile.types";
import { Skeleton } from "@/components/ui/skeleton";

interface UserProfileHoverCardProps {
  userId: string;
  children: React.ReactNode;
}

export function UserProfileHoverCard({ userId, children }: UserProfileHoverCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['user-profile-preview', userId],
    queryFn: async () => {
      // Query direta respeitando RLS (sem SECURITY DEFINER)
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select(`
          id,
          full_name,
          logo_url,
          linkedin_url
        `)
        .eq('id', userId)
        .maybeSingle();

      if (profileError) throw profileError;
      if (!profileData) throw new Error('Profile not found');

      // Buscar dados do candidato
      const { data: candidateData } = await supabase
        .from('candidates')
        .select('headline, bio, github_url, portfolio_url')
        .eq('profile_id', userId)
        .maybeSingle();

      // Buscar pontos e nível
      const { data: pointsData } = await supabase
        .from('user_points')
        .select('current_level, total_points')
        .eq('user_id', userId)
        .maybeSingle();

      // Contar posts e comentários
      const [{ count: postsCount }, { count: commentsCount }] = await Promise.all([
        supabase
          .from('community_posts')
          .select('*', { count: 'exact', head: true })
          .eq('author_id', userId),
        supabase
          .from('community_post_comments')
          .select('*', { count: 'exact', head: true })
          .eq('author_id', userId)
      ]);

      // Montar objeto no formato esperado
      return {
        id: profileData.id,
        full_name: profileData.full_name,
        logo_url: profileData.logo_url,
        headline: candidateData?.headline || null,
        bio_preview: candidateData?.bio ? candidateData.bio.substring(0, 150) : null,
        linkedin_url: profileData.linkedin_url,
        github_url: candidateData?.github_url || null,
        portfolio_url: candidateData?.portfolio_url || null,
        current_level: pointsData?.current_level || null,
        total_points: pointsData?.total_points || null,
        posts_count: postsCount || 0,
        comments_count: commentsCount || 0,
      } as UserProfilePreview;
    },
    enabled: isOpen,
  });

  const getInitials = (name: string | null) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <HoverCard openDelay={300} closeDelay={200} onOpenChange={setIsOpen}>
      <HoverCardTrigger asChild>
        {children}
      </HoverCardTrigger>
      <HoverCardContent className="w-80" side="top" align="start">
        {isLoading ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <Skeleton className="h-16 w-full" />
          </div>
        ) : profile ? (
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Avatar className="h-12 w-12 border">
                <AvatarImage 
                  src={profile.logo_url || undefined} 
                  alt={profile.full_name}
                  className="object-cover"
                />
                <AvatarFallback className="bg-primary/10">
                  <ImageIcon className="h-5 w-5 text-primary" />
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-sm truncate">{profile.full_name}</h4>
                  <LevelBadge userId={userId} />
                </div>
                {profile.headline && (
                  <p className="text-xs text-muted-foreground mt-1">{profile.headline}</p>
                )}
              </div>
            </div>

            {profile.bio_preview && (
              <p className="text-sm text-foreground/80 line-clamp-3">{profile.bio_preview}</p>
            )}

            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>{profile.posts_count} posts</span>
              <span>{profile.comments_count} comentários</span>
              {profile.total_points && <span>{profile.total_points} pontos</span>}
            </div>

            {(profile.linkedin_url || profile.github_url || profile.portfolio_url) && (
              <div className="flex items-center gap-2 pt-2 border-t">
                {profile.linkedin_url && (
                  <a
                    href={profile.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Linkedin className="h-4 w-4" />
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {profile.github_url && (
                  <a
                    href={profile.github_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Github className="h-4 w-4" />
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {profile.portfolio_url && (
                  <a
                    href={profile.portfolio_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Globe className="h-4 w-4" />
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Perfil não encontrado</p>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
